import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import axios from 'axios';

// Theme Colors
const themeColors = {
  primary: '#31C48D', // Green
  secondary: '#007BFF', // Blue
  background: '#F9FAFB',
  white: '#FFFFFF',
  grey: '#686978',
  darkGrey: '#4D4D4D',
  lightGrey: '#F1F1F1',
  error: '#FF3B30',
  success: '#31C48D',
};

// API Configuration
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

// Sidebar Login Component
const LoginSidebar = ({ open, onClose, setLoggedIn }) => {
  const [step, setStep] = useState(1); // 1: Mobile Input, 2: OTP Input
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpToken, setOtpToken] = useState(''); // Token received from OTP API
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Handle Mobile Input
  const handleMobileChange = (e) => setMobileNumber(e.target.value);

  // Validate Mobile Number
  const isValidMobile = () => /^\d{10}$/.test(mobileNumber);

  // Send OTP API Call
  const handleMobileSubmit = async () => {
    if (!isValidMobile()) {
      setErrorMessage('Enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/users/login`, {
        mobile: mobileNumber,
        auth_type: 'OTP',
        delivery_type: 'TEXT',
        retry: false,
      });

      if (response.data.status) {
        setOtpToken(response.data.data.otp_token); // OTP token for verification
        setStep(2); // Move to OTP step
        setSuccessMessage('OTP sent successfully!');
      } else {
        setErrorMessage(response.data.message || 'Failed to send OTP.');
      }
    } catch (error) {
      setErrorMessage('Failed to send OTP. Try again!');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP Input
  const handleOtpChange = (index, value) => {
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 3) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };

  // Verify OTP API Call
  const handleOtpSubmit = async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 4) {
      setErrorMessage('Enter a valid 4-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/users/verify-otp`, {
        mobile: mobileNumber,
        otp_token: otpToken,
        otp: enteredOtp,
      });

      if (response.data.status) {
        const userData = response.data.data;

        // Store user details in localStorage
        localStorage.setItem('authToken', userData.session_token);
        localStorage.setItem('externalId', userData.external_id);
        localStorage.setItem('email', userData.email);
        localStorage.setItem('profilePic', userData.profile_pic);

        // Notify parent about login
        setLoggedIn(true);

        // Close Sidebar
        setSuccessMessage('Login successful!');
        onClose();
      } else {
        setErrorMessage(response.data.message || 'Invalid OTP. Try again!');
      }
    } catch (error) {
      setErrorMessage('Invalid OTP. Try again!');
    } finally {
      setLoading(false);
    }
  };

  // Close Sidebar
  const handleClose = () => {
    setStep(1);
    setMobileNumber('');
    setOtp(['', '', '', '']);
    setOtpToken('');
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    if (token) {
      setLoggedIn(true);
    }
  }, [setLoggedIn]);

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose}>
        {/* Header */}
        <Box
          sx={{
            height: '200px',
            backgroundColor: themeColors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Typography variant="h5" sx={{ color: themeColors.white, fontWeight: 'bold' }}>
            Welcome Back!
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: themeColors.white,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ padding: '24px', minWidth: '400px' }}>
          {step === 1 ? (
            <>
              <Typography variant="h6" sx={{ marginBottom: '16px' }}>
                Login with Mobile Number
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter mobile number"
                value={mobileNumber}
                onChange={handleMobileChange}
                variant="outlined"
                inputProps={{ maxLength: 10 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleMobileSubmit}
                sx={{ marginTop: '16px', backgroundColor: themeColors.primary }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ marginBottom: '16px' }}>
                Enter OTP
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                {otp.map((digit, index) => (
                  <Grid item key={index}>
                    <TextField
                      id={`otp-${index}`}
                      variant="outlined"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                      sx={{ width: '48px' }}
                    />
                  </Grid>
                ))}
              </Grid>
              <Button
                variant="contained"
                fullWidth
                onClick={handleOtpSubmit}
                sx={{ marginTop: '16px', backgroundColor: themeColors.primary }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default LoginSidebar;